/**
 * <shopla-ships-to> — the shipping-status line of the ships-to block:
 * "Ships to {region}" in green where the product ships, "Does not ship to
 * {region}" in red where it doesn't (the `is-shippable` / `is-not-shippable`
 * classes; same palette as the opening-hours status).
 *
 * Two pieces of state meet here:
 *
 * - The DESTINATION — the shipping region of the visitor's shipping address —
 *   is client-side state owned by the map-search bar (map-search.js): the
 *   selected account shipping address's province/country, else the region
 *   resolved from the visitor's current location. The bar persists it under
 *   its localStorage key and broadcasts changes as `shopla:ships-to:changed`
 *   ({region: display name, zone: 'CC' or ISO 3166-2 'CC-PP'}); this element
 *   reads the persisted copy on connect and follows the events, so the line is
 *   right even on pages whose bar hasn't resolved anything yet this session —
 *   and updates in place when the visitor picks another shipping address.
 *
 * - The product's COVERAGE — where it actually ships — is server state: the
 *   block serializes the variants' `shopla.ships-to` metafields into
 *   data-coverage. The line's state follows whether the destination zone is
 *   covered: a product that doesn't ship to the visitor's region must not
 *   claim "Ships to {region}" — it wouldn't be returned by a shipping-mode
 *   search for that zone either.
 *
 * While no region display name is known ('' — first visit, storage
 * unavailable, geocoder couldn't name one) the destination is named at the
 * country level: the storefront's selected country (data-country-name), or
 * the zone's country via Intl where the persisted zone points elsewhere —
 * still a truthful destination, just coarser.
 *
 * In the block's popup variant (signed in, product context) the element also
 * hosts the "Details" dialog's per-saved-address rows, rendered entirely in
 * Liquid, with two dynamic parts:
 *
 * - Highlighting the currently selected shipping address (`is-current` +
 *   aria-pressed). That selection is persisted by the bar as
 *   `delivery: {source: 'account', id}` under the same localStorage key —
 *   read on connect, when the dialog opens, and on the bar's change
 *   broadcasts, so a selection made in the Shipping popover is reflected
 *   without a reload. A stale id (deleted address) simply matches no row.
 *
 * - Selecting: each row is a button carrying the popover row's data-*
 *   contract; clicking it makes that address the current shipping address.
 *   The click is delegated to the bar's own popover row so the bar stays the
 *   single owner of the selection (in-memory state, persistence, highlight,
 *   region broadcast, shipping-mode re-search); only when no bar is on the
 *   page does this element persist the choice and broadcast the change
 *   itself.
 */

import { countryDisplayName } from '@theme/province-resolver';

/** The map-search bar's persisted-preferences key — see map-search.js. */
const STORAGE_KEY = 'shopla:map-search';

class ShoplaShipsTo extends HTMLElement {
  #controller = new AbortController();

  /** The destination's display name, '' while none is known. */
  #region = '';

  /** The destination zone ('CC' or 'CC-PP') the coverage check runs against. */
  #zone = '';

  connectedCallback() {
    const persisted = this.#persisted();
    this.#region = persisted.region;
    this.#zone = persisted.zone || (this.dataset.country ?? '').toUpperCase();
    this.#render();
    this.#applySelection();

    document.addEventListener('shopla:ships-to:changed', this.#handleChanged, {
      signal: this.#controller.signal,
    });
    // Refresh the selected-address highlight when the dialog opens (dialog:open is
    // dispatched on the wrapping <dialog-component> without bubbling) — together with the
    // ships-to-changed events this catches a selection made in the Shipping popover since
    // the page rendered.
    this.closest('dialog-component')?.addEventListener('dialog:open', this.#handleDialogOpen, {
      signal: this.#controller.signal,
    });
    // The dialog's address rows select on click (delegated — the rows are re-checked on
    // every click so this also covers rows swapped in by a future re-render).
    this.addEventListener('click', this.#handleRowClick, { signal: this.#controller.signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /** @param {Event} event */
  #handleChanged = (event) => {
    if (!(event instanceof CustomEvent)) return;
    const { region, zone } = event.detail ?? {};
    if (typeof zone === 'string' && zone) this.#zone = zone.toUpperCase();
    if (typeof region === 'string') this.#region = region;
    this.#render();
    this.#applySelection();
  };

  #handleDialogOpen = () => this.#applySelection();

  /** @param {Event} event */
  #handleRowClick = (event) => {
    const row =
      event.target instanceof Element
        ? event.target.closest('.shopla-ships-to__row[data-address-id]')
        : null;
    if (row instanceof HTMLElement && this.contains(row)) this.#selectAddress(row);
  };

  /**
   * Makes the clicked row's address the current shipping address — the same
   * selection as clicking that address in the map-search bar's Shipping
   * popover. Preferably by clicking the bar's own popover row for this
   * address id, so the bar's selectDeliveryAddress runs and the bar remains
   * the single owner of the selection (its in-memory state, persistence,
   * popover highlight, region broadcast and shipping-mode re-search all
   * happen exactly as from the popover). Only when no bar is on the page is
   * the choice persisted and broadcast from here. Either way this element's
   * own line and row highlight follow immediately — the broadcast alone
   * would miss a switch between addresses whose regions share a display
   * name.
   *
   * @param {HTMLElement} row
   */
  #selectAddress(row) {
    const id = row.dataset.addressId ?? '';
    const zone = (row.dataset.shipsTo || '').toUpperCase();
    if (!id || !zone) return;
    const region = row.dataset.region ?? '';

    const barRow = document.querySelector(
      `button.map-search__delivery-address[data-address-id="${CSS.escape(id)}"]`
    );
    if (barRow instanceof HTMLElement) {
      barRow.click();
    } else {
      this.#persistSelection(id, zone, region);
      document.dispatchEvent(
        new CustomEvent('shopla:ships-to:changed', { detail: { region, zone } })
      );
    }

    this.#zone = zone;
    this.#region = region;
    this.#render();
    this.#applySelection();
  }

  /**
   * Fallback persistence when no map-search bar is on the page: merges the
   * selection into the bar's persisted preferences (delivery + the zone and
   * region it owns), so the bar picks it up on the next page that has one.
   * Best-effort — storage may be unavailable.
   *
   * @param {string} id
   * @param {string} zone
   * @param {string} region
   */
  #persistSelection(id, zone, region) {
    try {
      let data = {};
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') data = parsed;
      }
      data.delivery = { source: 'account', id, shipsTo: zone };
      data.shipsTo = zone;
      data.shipsToRegion = region;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {
      // Persistence is best-effort; ignore storage failures.
    }
  }

  /** @returns {HTMLElement | null} */
  #textElement() {
    return this.querySelector('.shopla-ships-to__text');
  }

  /**
   * The persisted shipping destination — region display name and zone — or
   * empty strings when there is none (nothing stored yet, storage
   * unavailable, or junk).
   *
   * @returns {{ region: string, zone: string }}
   */
  #persisted() {
    const none = { region: '', zone: '' };
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return none;
      const data = JSON.parse(raw);
      if (!data || typeof data !== 'object') return none;
      return {
        region: typeof data.shipsToRegion === 'string' ? data.shipsToRegion : '',
        zone: typeof data.shipsTo === 'string' ? data.shipsTo.toUpperCase() : '',
      };
    } catch {
      return none;
    }
  }

  /**
   * The persisted selected account shipping address id (the bar's `delivery`
   * payload), or null while the automatic location-derived zone stands
   * (nothing stored, storage unavailable, or junk).
   *
   * @returns {string | null}
   */
  #selectedAddressId() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const data = JSON.parse(raw);
      const delivery = data && typeof data === 'object' ? data.delivery : null;
      if (!delivery || typeof delivery !== 'object') return null;
      if (delivery.source !== 'account' || delivery.id == null) return null;
      return String(delivery.id);
    } catch {
      return null;
    }
  }

  /**
   * Marks the dialog row of the currently selected shipping address as
   * `is-current` (+ aria-pressed, the rows being toggle buttons) — the popup
   * variant's counterpart of the Shipping popover's own selection highlight.
   * A no-op in the plain line variant (no rows).
   */
  #applySelection() {
    const rows = this.querySelectorAll('.shopla-ships-to__row[data-address-id]');
    if (rows.length === 0) return;

    const id = this.#selectedAddressId();
    for (const row of rows) {
      const selected = id !== null && row instanceof HTMLElement && row.dataset.addressId === id;
      row.classList.toggle('is-current', selected);
      row.setAttribute('aria-pressed', String(selected));
    }
  }

  /**
   * The product's shipping coverage: the compact `shopla.ships-to` entries
   * across its variants, from data-coverage. null when the attribute is
   * absent (no product context, or markup predating the coverage check) —
   * coverage is unknown and the line stays shippable.
   *
   * @returns {string[] | null}
   */
  #coverageEntries() {
    const raw = this.getAttribute('data-coverage');
    if (raw === null) return null;
    return raw
      .toUpperCase()
      .split(',')
      .map((entry) => entry.trim())
      .filter(Boolean);
  }

  /**
   * Whether the product ships to `zone`, mirroring how the search index
   * unions the variants' entries (collectShipsTo in shopla-search-shopify)
   * and how the search service then matches a zone, so the line agrees with
   * shipping-mode search results. The compact entry forms:
   *
   *   "*"           rest of world — covers any zone
   *   "CH"          the whole country
   *   "RU-NEN-YAN"  the whole country EXCEPT the listed provinces
   *   "RU+MOW+SPE"  ONLY the listed provinces
   *
   * Unioned per country: a whole-country entry covers everything; a province
   * is excluded only when EVERY exclusive entry drops it and no inclusive
   * entry ships to it — and an exclusion beats rest-of-world, exactly as the
   * indexed ships_to_excluded field does in the search filter. A bare-country
   * zone is not covered by inclusive-only entries (the index carries only the
   * listed 'CC-PP' codes for them, which a 'CC' search doesn't match).
   *
   * @param {string} zone
   * @returns {boolean}
   */
  #covers(zone) {
    const entries = this.#coverageEntries();
    if (entries === null) return true;
    if (entries.length === 0) return false;
    if (!zone) return true;

    const [zoneCountry, zoneProvince = ''] = zone.split('-');

    let restOfWorld = false;
    let whole = false;
    /** @type {string[][]} */
    const exclusiveSets = [];
    const included = new Set();

    for (const entry of entries) {
      if (entry === '*') {
        restOfWorld = true;
        continue;
      }
      const plus = entry.indexOf('+');
      const dash = entry.indexOf('-');
      const cut = plus !== -1 ? plus : dash;
      const country = cut === -1 ? entry : entry.slice(0, cut);
      if (country !== zoneCountry) continue;
      if (cut === -1) {
        whole = true;
      } else if (plus !== -1) {
        for (const p of entry.slice(plus + 1).split('+')) if (p) included.add(p);
      } else {
        exclusiveSets.push(entry.slice(dash + 1).split('-').filter(Boolean));
      }
    }

    const positive =
      restOfWorld ||
      whole ||
      exclusiveSets.length > 0 ||
      (zoneProvince !== '' && included.has(zoneProvince));
    if (!positive) return false;

    if (!whole && zoneProvince !== '' && exclusiveSets.length > 0) {
      const excluded =
        exclusiveSets.every((set) => set.includes(zoneProvince)) && !included.has(zoneProvince);
      if (excluded) return false;
    }
    return true;
  }

  /**
   * The destination's display name for the line: the region name when one is
   * known; otherwise the destination's country — the server-rendered
   * storefront country name when the zone still points there, else the
   * zone's country named via Intl (falling back to the bare code).
   *
   * @returns {string}
   */
  #regionName() {
    if (this.#region) return this.#region;
    const zoneCountry = this.#zone.split('-')[0];
    if (zoneCountry === (this.dataset.country ?? '').toUpperCase()) {
      return this.dataset.countryName || zoneCountry;
    }
    return countryDisplayName(zoneCountry, document.documentElement.lang) || zoneCountry;
  }

  /**
   * Renders the line for the current destination and coverage: the shippable
   * or not-shippable label with `{region}` filled, and the matching state
   * class (green/red — see the block's stylesheet).
   */
  #render() {
    const covered = this.#covers(this.#zone);
    this.classList.toggle('is-shippable', covered);
    this.classList.toggle('is-not-shippable', !covered);

    const target = this.#textElement();
    if (!target) return;
    const label = covered
      ? this.dataset.label || 'Ships to {region}'
      : this.dataset.labelNegative || 'Does not ship to {region}';
    target.textContent = label.replace('{region}', this.#regionName());
  }
}

if (!customElements.get('shopla-ships-to')) {
  customElements.define('shopla-ships-to', ShoplaShipsTo);
}
